import { useEffect, useState, useMemo } from "react";
import { getDashboardPets } from "../../api/axios";
import { nf, perc } from "../../utilities/Utils";
import useAuth from "../../hooks/useAuth";
import LoadingContainer from "../../components/LoadingContainer";
import {
  DashboardBars,
  DashboardMetric,
  DashboardPanel,
  DashboardSplitBars,
  getCountValue,
  getSourceLabel,
  sortByValue,
} from "./DashboardVisuals";

const DEFAULT_SUMMARY = { total: 0, dogs: 0, cats: 0 };

const DashboardPetsTable = ({ params }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [petsSummary, setPetsSummary] = useState(DEFAULT_SUMMARY);
  const [dogAgeSummary, setDogAgeSummary] = useState([]);
  const [catAgeSummary, setCatAgeSummary] = useState([]);
  const [dogSizeSummary, setDogSizeSummary] = useState([]);
  const [dogsByBreed, setDogsByBreed] = useState([]);
  const [catsByBreed, setCatsByBreed] = useState([]);
  const [sourcesSummary, setSourcesSummary] = useState([]);
  const [activationsSummary, setActivationsSummary] = useState([]);
  const [eventsSummary, setEventsSummary] = useState([]);
  const { auth } = useAuth();

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);
  const dashboardParams = useMemo(() => JSON.parse(paramsKey), [paramsKey]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const [
          speciesRes,
          dogAgeRes,
          catAgeRes,
          dogSizeRes,
          dogsBreedRes,
          catsBreedRes,
          sourcesRes,
          activationsRes,
          eventsRes,
        ] = await Promise.all([
          getDashboardPets({ auth, params: { ...dashboardParams, bySpecies: true } }),
          getDashboardPets({ auth, params: { ...dashboardParams, byDogAge: true } }),
          getDashboardPets({ auth, params: { ...dashboardParams, byCatAge: true } }),
          getDashboardPets({ auth, params: { ...dashboardParams, byDogSize: true } }),
          getDashboardPets({ auth, params: { ...dashboardParams, hasDog: true, byBreed: true } }),
          getDashboardPets({ auth, params: { ...dashboardParams, hasCat: true, byBreed: true } }),
          getDashboardPets({ auth, params: { ...dashboardParams, bySource: true, idParentSource: 0 } }),
          getDashboardPets({ auth, params: { ...dashboardParams, bySource: true, idParentSource: 11 } }),
          getDashboardPets({ auth, params: { ...dashboardParams, bySource: true, idParentSource: 6 } }),
        ]);

        const species = Array.isArray(speciesRes?.results) ? speciesRes.results : [];
        const total = species.reduce((sum, item) => sum + getCountValue(item), 0);
        const dogs = getCountValue(species.find((item) => item?.key === "perro"));
        const cats = getCountValue(species.find((item) => item?.key === "gato"));

        setPetsSummary({ total, dogs, cats });
        setDogAgeSummary(sortByValue(Array.isArray(dogAgeRes?.results) ? dogAgeRes.results : []));
        setCatAgeSummary(sortByValue(Array.isArray(catAgeRes?.results) ? catAgeRes.results : []));
        setDogSizeSummary(sortByValue(Array.isArray(dogSizeRes?.results) ? dogSizeRes.results : []));
        setDogsByBreed(sortByValue(Array.isArray(dogsBreedRes?.results) ? dogsBreedRes.results : []));
        setCatsByBreed(sortByValue(Array.isArray(catsBreedRes?.results) ? catsBreedRes.results : []));
        setSourcesSummary(Array.isArray(sourcesRes?.results) ? sourcesRes.results : []);
        setActivationsSummary(Array.isArray(activationsRes?.results) ? activationsRes.results : []);
        setEventsSummary(Array.isArray(eventsRes?.results) ? eventsRes.results : []);
        setError(null);
      } catch (err) {
        setError(err);
      }

      setIsLoading(false);
    };

    fetchData();
  }, [auth, dashboardParams]);

  if (error) {
    return (
      <div className="dashboard-overview dashboard-detail">
        <div className="dashboard-empty">No se pudieron cargar los indicadores de mascotas.</div>
      </div>
    );
  }

  return (
    <LoadingContainer isLoading={isLoading}>
      <div className="dashboard-overview dashboard-detail">
        <section className="dashboard-hero">
          <div className="dashboard-hero__intro">
            <span className="dashboard-eyebrow">Mascotas</span>
            <h2 className="dashboard-hero__title">Composición de perros y gatos</h2>
            <p className="dashboard-hero__copy">
              Volumen, razas, etapas de vida y fuentes de captación de la base de mascotas.
            </p>
          </div>
          <div className="dashboard-hero__metrics">
            <DashboardMetric label="Total mascotas" value={petsSummary.total} detail="registros activos" accent="red" />
            <DashboardMetric label="Perros" value={petsSummary.dogs} detail={perc(petsSummary.total, petsSummary.dogs)} accent="gold" />
            <DashboardMetric label="Gatos" value={petsSummary.cats} detail={perc(petsSummary.total, petsSummary.cats)} accent="ink" />
            <DashboardMetric label="Razas vistas" value={dogsByBreed.length + catsByBreed.length} detail="con registros" accent="sand" />
          </div>
        </section>

        <section className="dashboard-grid dashboard-detail-grid">
          <DashboardPanel kicker="Origen" title="Fuentes" className="dashboard-panel--feature">
            <DashboardSplitBars items={sourcesSummary} getLabel={getSourceLabel} limit={10} dogKey="dogs" catKey="cats" />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Activaciones">
            <DashboardSplitBars items={activationsSummary} getLabel={getSourceLabel} limit={10} dogKey="dogs" catKey="cats" />
          </DashboardPanel>

          <DashboardPanel kicker="Origen" title="Eventos">
            <DashboardSplitBars items={eventsSummary} getLabel={getSourceLabel} limit={10} dogKey="dogs" catKey="cats" />
          </DashboardPanel>

          <DashboardPanel kicker="Perros" title="Etapas de vida">
            <DashboardBars
              items={dogAgeSummary}
              getLabel={(item) => item.label ?? "Sin etapa"}
              getValue={getCountValue}
              tone="gold"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Perros" title="Talla">
            <DashboardBars
              items={dogSizeSummary}
              getLabel={(item) => item.label ?? "Sin talla"}
              getValue={getCountValue}
              tone="sand"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Gatos" title="Etapas de vida">
            <DashboardBars
              items={catAgeSummary}
              getLabel={(item) => item.label ?? "Sin etapa"}
              getValue={getCountValue}
              tone="ink"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Perros" title="Razas principales" className="dashboard-panel--feature">
            <DashboardBars
              items={dogsByBreed}
              getLabel={(item) => item.label ?? "Sin raza"}
              getValue={getCountValue}
              limit={12}
              tone="gold"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Gatos" title="Razas principales">
            <DashboardBars
              items={catsByBreed}
              getLabel={(item) => item.label ?? "Sin raza"}
              getValue={getCountValue}
              limit={12}
              tone="ink"
            />
          </DashboardPanel>

          <DashboardPanel kicker="Síntesis" title="Lectura operativa">
            <div className="dashboard-scoreboard">
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Mascotas</span>
                <strong className="dashboard-scoreboard__value">{nf(petsSummary.total)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Perros</span>
                <strong className="dashboard-scoreboard__value">{nf(petsSummary.dogs)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Gatos</span>
                <strong className="dashboard-scoreboard__value">{nf(petsSummary.cats)}</strong>
              </div>
              <div className="dashboard-scoreboard__item">
                <span className="dashboard-scoreboard__label">Fuentes</span>
                <strong className="dashboard-scoreboard__value">{sourcesSummary.length}</strong>
              </div>
            </div>
          </DashboardPanel>
        </section>
      </div>
    </LoadingContainer>
  );
};

export default DashboardPetsTable;
